import type { Key } from 'react';
import dayjs from 'dayjs';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { DATE_FORMAT } from '@/std/const/date';
import { createPaginationStore } from '@/std/pagination/createPaginationStore';
import type { FormNotaFiscalProduto } from './page/edicao/type/types';
import type { NotaFiscal } from './types/NotaFiscal';
import type { NotaFiscalPartial } from './types/NotaFiscalPartial';

type NotaFiscalStore = {
    drawerDetalhesTab: string;
    showDrawerGetDetalhes: boolean;
    showDrawerEstornoNf: boolean;
    selectedRow: NotaFiscalPartial | null;
    selectedRows: NotaFiscalPartial[];
    selectedRowKeys: Key[];

    clearSelectedRows: () => void;

    drawerProduto: {
        show: boolean;
        item: FormNotaFiscalProduto | null;
        mode: 'cadastrar' | 'editar';
    };
    novoProduto: (item: FormNotaFiscalProduto) => void;
    editarProduto: (item: FormNotaFiscalProduto) => void;
    closeDrawerProduto: () => void;
};

const initialStateDrawerProduto: NotaFiscalStore['drawerProduto'] = {
    item: null,
    mode: 'cadastrar',
    show: false,
};

// biome-ignore assist/source/useSortedKeys: keep order
export const useNotaFiscalStore = create<NotaFiscalStore>()((set) => ({
    drawerDetalhesTab: '',
    showDrawerGetDetalhes: false,
    showDrawerEstornoNf: false,
    selectedRow: null,
    selectedRows: [],
    selectedRowKeys: [],

    clearSelectedRows: () => {
        set({
            selectedRow: null,
            selectedRowKeys: [],
            selectedRows: [],
        });
    },

    closeDrawerProduto: (): void => {
        set({ drawerProduto: initialStateDrawerProduto });
    },

    drawerProduto: initialStateDrawerProduto,

    editarProduto: (item: FormNotaFiscalProduto): void => {
        set({
            drawerProduto: {
                item,
                mode: 'editar',
                show: true,
            },
        });
    },

    novoProduto: (item: FormNotaFiscalProduto): void => {
        set({
            drawerProduto: {
                item,
                mode: 'cadastrar',
                show: true,
            },
        });
    },
}));

export type NotaFiscalFilter = {
    pesquisar: string;
    data_inicio: string;
    data_fim: string;
    numero: number;
    serie: number;
};

export const useNotaFiscalPaginationStore = createPaginationStore<NotaFiscal, NotaFiscalFilter>({
    apiPagination: {
        orderby: 'cast(nof_numero as float) desc',
    },
    fieldMap: new Map([['nof_numero', 'cast(nof_numero as float)']]),
    filter: {
        data_fim: dayjs().format(DATE_FORMAT),
        data_inicio: dayjs().subtract(1, 'month').format(DATE_FORMAT),
    },
});

export const useNotaFiscalPagination = () =>
    useNotaFiscalPaginationStore(
        useShallow((s) => ({
            apiPagination: s.apiPagination,
            filter: s.filter,
            onTableChange: s.onTableChange,
            setFilter: s.setFilter,
            tablePagination: s.tablePagination,
            updateTotalAndPagination: s.updateTotalAndPagination,
        })),
    );

export const resetNotaFiscalPaginationOrderBy = () => {
    useNotaFiscalPaginationStore.setState((state) => ({
        apiPagination: {
            ...state.apiPagination,
            orderby: 'nof_data_emissao desc',
        },
    }));
};
